import React, { Component } from 'react';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import _ from 'lodash';
import cx from 'classnames';
import vex from 'lib/vex';
import { connect } from 'react-redux';
import { DefaultSpinner, ErrorPlaceholder } from 'components';

import AppData from 'app_data';
import CustomReportTile, { CustomReport } from 'modules/monitoring/custom_dashboard/custom_report_tile';
import CallTypeTile, { CallType } from 'modules/monitoring/custom_dashboard/call_type_tile';
import GamificationTile, { Gamification } from 'modules/monitoring/custom_dashboard/gamification_tile';
import { mapStateToProps, mapDispatchToProps } from 'modules/monitoring/custom_dashboard/connector';
import { CustomDashboardControls, CustomDashboardTile } from 'components/custom_dashboard';
import ButtonPopover from 'components/button_popover';
import Utils from 'utils/common_utils';
import type {
  CustomDashboardState,
  DashboardTile,
} from 'reducers/custom_dashboard_reducer';

const ReactGridLayout = WidthProvider(GridLayout);

// custom_dashboard_tile.js spreads its rest props onto the div, so onMouseDown
// is supported; its propTypes just do not declare it.
const DashboardTileView = CustomDashboardTile as React.ComponentType<{
  className?: string;
  onMouseDown?: (e: React.MouseEvent) => void;
  children?: React.ReactNode;
}>;

/** One entry of GET /nextos/reports/custom, as the report tile picker needs it. */
export interface CustomReportOption {
  report_id: string;
  report_name: string;
  format: string;
}

/**
 * The bound creators from custom_dashboard/connector. Typed explicitly rather
 * than as a generic bag because handleSave chains off saveDashboard's promise.
 */
export interface CustomDashboardActions {
  addTile: (...args: unknown[]) => void;
  breakpointChange: (breakpoint: string, cols: number) => void;
  cancel: (elementIndex: number) => void;
  cancelNew: (elementIndex: number) => void;
  clear: () => void;
  editTile: (elementIndex: number) => void;
  layoutChange: (layout: unknown[]) => void;
  loadDashboard: (id: string | number, viewing?: boolean) => void;
  loadDashboardFailure: (error: unknown) => void;
  removeTile: (elementIndex: number) => void;
  saveDashboard: (dashboardId?: string | number) => Promise<void>;
  setDescription: (value: string) => void;
  setRealTime: (value: boolean) => void;
  setTile: (tileType: string, elementIndex: number) => void;
  setTimeRange: (value: unknown) => void;
  setTitle: (value: string) => void;
  startNewDashboard: () => void;
  toggleEditDashboard: () => void;
  updateTile: (state: Record<string, unknown>, elementIndex: number) => void;
  validate: (options: { excludeTitle?: boolean }) => string[] | null;
}

export interface CustomDashboardProps {
  className?: string;
  cols?: number;
  rowHeight?: number;
  useCSSTransforms?: boolean;
  /** Present in defaultProps but never declared in the old propTypes. */
  verticalCompact?: boolean;
  customDashboardActions: CustomDashboardActions;
  custom_dashboard: CustomDashboardState;
  match: { params: { dashboardId?: string } };
  push?: (path: string) => void;
  viewing?: boolean;
  isShowIconPopout?: boolean;
  isShowEditButton?: boolean;
  location: { search: string };
}

interface CustomDashboardComponentState {
  reports?: CustomReportOption[];
  /** Read by onMouseDown; never written by this component. */
  isEditing?: boolean;
}

class CustomDashboard extends Component<
  CustomDashboardProps,
  CustomDashboardComponentState
> {
  static defaultProps = {
    className: 'dashboard_layout',
    cols: 12,
    rowHeight: 100,
    useCSSTransforms: true,
    verticalCompact: true,
    isShowIconPopout: false,
    isShowEditButton: true,
  };

  state: CustomDashboardComponentState = {};

  componentDidMount() {
    const { location, customDashboardActions } = this.props;
    const notAllowed = ['call_log', 'period'];

    AppData.getCustomReports()
      .then((response: { data: CustomReportOption[] }) => {
        const { data } = response;
        const reports = data.filter((report) => !notAllowed.includes(report.format));
        this.setState({ reports });
        this.loadDashboard();

        const params = new URLSearchParams(location.search);
        customDashboardActions.setRealTime(params.get('realTime') === 'true');
      })
      // The reports list is a PREREQUISITE: loadDashboard() is only called
      // from the success path above, so without this a failed list means
      // neither LOAD_DASHBOARD_REQUEST nor _FAILURE ever dispatches and
      // isLoading -- which starts true -- never clears. Cannot double-report:
      // both loadDashboard() and startNewDashboard() already dispatch _FAILURE
      // from their own catch, and this.loadDashboard()'s return value is
      // deliberately dropped above, so neither rejection can reach this chain.
      // Keep it dropped -- returning it would report the same failure twice.
      // What is left for this catch is a failed list, or a throw in the
      // handler itself.
      .catch((error: unknown) => {
        customDashboardActions.loadDashboardFailure(error);
      });
  }

  shouldComponentUpdate(
    nextProps: CustomDashboardProps,
    nextState: CustomDashboardComponentState
  ) {
    const propsChanged = !_.isEqual(nextProps, this.props);
    const stateChanged = !_.isEqual(nextState, this.state);
    return propsChanged || stateChanged;
  }

  componentWillUnmount() {
    this.props.customDashboardActions.clear();
  }

  // We're using the cols coming back from this to calculate where to add new items.
  onBreakpointChange = (breakpoint: string, cols: number) => {
    this.props.customDashboardActions.breakpointChange(breakpoint, cols);
  };

  onLayoutChange = (layout: unknown[]) => {
    const { custom_dashboard: uiState } = this.props;
    if (!_.isEqual(uiState.layout, layout)) {
      this.props.customDashboardActions.layoutChange(layout);
      if (this.props.viewing && !uiState.isEditingTile) {
        this.handleSave();
      }
    }
  };

  onRemoveItem = (elementIndex: number) => {
    if (this.props.viewing) {
      if (this.props.custom_dashboard.items.length > 1) {
        this.props.customDashboardActions.removeTile(elementIndex);
        this.handleSave();
        return;
      }
      vex.error(['One tile must be left in a dashboard.']);
    } else {
      this.props.customDashboardActions.removeTile(elementIndex);
    }
  };

  onUpdateItem = (state: Record<string, unknown>, elementIndex: number) => {
    this.props.customDashboardActions.updateTile(state, elementIndex);
    if (this.props.viewing) {
      this.handleSave();
    }
  };

  onEditItem = (elementIndex: number) => {
    if (this.props.custom_dashboard.isEditingTile) {
      return;
    }
    this.props.customDashboardActions.editTile(elementIndex);
  };

  onCancel = (elementIndex: number) => {
    this.props.customDashboardActions.cancel(elementIndex);
  };

  onCancelNew = (elementIndex: number) => {
    this.props.customDashboardActions.cancelNew(elementIndex);
  };

  onMouseDown = (e: React.MouseEvent) => {
    if (!this.state.isEditing) {
      e.stopPropagation();
    }
  };

  getPopoverContent = (el: DashboardTile, elementIndex: number) => {
    let content;
    let options;
    const self = this;
    const tileType = el.tileType;
    const tileInfo = el.tileInfo || {};
    const removeHandler = this.onRemoveItem.bind(this, elementIndex);
    const updateHandler = function (state: Record<string, unknown>) { self.onUpdateItem(state, elementIndex); };
    const cancelHandler = el.isNew ?
      this.onCancelNew.bind(this, elementIndex) :
      this.onCancel.bind(this, elementIndex);

    switch (tileType) {
      case 'reports':
        options = (this.state.reports as CustomReportOption[]).map((report) =>
          <option key={report.report_id} value={report.report_id}>{report.report_name}</option>
        );
        content = (
          <CustomReportTile
            options={this.state.reports}
            onRemoveItem={removeHandler}
            onUpdateItem={updateHandler}
            tileInfo={tileInfo}
            onCancel={cancelHandler}
          />
        );
        break;
      case 'call_type':
        content = (
          <CallTypeTile
            onRemoveItem={removeHandler}
            onUpdateItem={updateHandler}
            tileInfo={tileInfo}
            onCancel={cancelHandler}
          />
        );
        break;
      case 'gamification':
        content = (
          <GamificationTile
            onRemoveItem={removeHandler}
            onUpdateItem={updateHandler}
            tileInfo={tileInfo}
            onCancel={cancelHandler}
          />
        );
        break;
      default:
        content = (
          <div>
            <div className="custom_dashboard_popover_title">Add element</div>
            <div className="custom_dashboard_popover_content">
              <p>Reports</p>
              <input type="button" className="btn" onClick={this.setTile.bind(this, 'reports', elementIndex)} value="Saved Reports" />
              <p>Monitoring</p>
              <input type="button" className="btn" onClick={this.setTile.bind(this, 'call_type', elementIndex)} value="Call Type" />
              <input type="button" className="btn" onClick={this.setTile.bind(this, 'gamification', elementIndex)} value="Gamification" />
            </div>
          </div>
        );
    }
    return content;
  };

  getTileContent = (el: DashboardTile, index: number) => {
    let content;
    const { custom_dashboard: uiState } = this.props;
    const tileType = el.isNew ? 'new' : el.tileType;
    const editHandler = this.onEditItem.bind(this, index);
    const updateHandler = _.bind(this.onUpdateItem, this, _, index);

    const popoverContent = this.getPopoverContent(el, index);

    let outerAction: () => void = () => {};

    if (!el.isNew) {
      outerAction = this.onCancel.bind(this, index);
    }

    const editButton = (
      <ButtonPopover
        popoverClass="custom_dashboard_popover"
        iconClass="icon-custom-gear"
        buttonClass="btn-transparent custom-dashboard__tile__action edit"
        popoverBody={popoverContent}
        isOpen={el.isEditing}
        onOuterAction={outerAction}
        onClick={editHandler}
      />
    );

    switch (tileType) {
      case 'reports':
        content = (
          <CustomReport
            tileInfo={el.tileInfo}
            timeRange={uiState.timeRange}
            realTime={uiState.realTime}
            onUpdateItem={updateHandler}
            editButton={editButton}
          />
        );
        break;
      case 'call_type':
        content = (
          <CallType
            tileInfo={el.tileInfo}
            timeRange={uiState.timeRange}
            realTime={uiState.realTime}
            editButton={editButton}
          />
        );
        break;
      case 'gamification':
        content = (
          <Gamification
            tileInfo={el.tileInfo as React.ComponentProps<typeof Gamification>['tileInfo']}
            timeRange={uiState.timeRange as React.ComponentProps<typeof Gamification>['timeRange']}
            realTime={uiState.realTime}
            editButton={editButton}
          />
        );
        break;
      default:
        content = (
          <div className="custom_dashboard_tile_content">
            <div className="custom_dashboard_actions">
              {editButton}
            </div>
            <span className="grid-item-text">{el.content}</span>
          </div>
        );
        break;
    }

    return content;
  };

  onOpenPopout = () => {
    const {
      match,
      custom_dashboard: uiState,
    } = this.props;
    const { dashboardId } = match.params;

    const params = uiState.realTime ? '?realTime=true' : '';
    Utils.openRoute(`/popout/custom-dashboard/${dashboardId}${params}`);
  };

  setTile = (value: string, elementIndex: number) => {
    this.props.customDashboardActions.setTile(value, elementIndex);
  };

  loadDashboard = () => {
    const { dashboardId } = this.props.match.params;
    const { viewing } = this.props;

    if (dashboardId) {
      this.props.customDashboardActions.loadDashboard(dashboardId, viewing);
    } else {
      this.props.customDashboardActions.startNewDashboard();
    }
  };

  handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.customDashboardActions.setTitle(e.target.value);
  };

  handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.props.customDashboardActions.setDescription(e.target.value);
  };

  handleTimeRangeChange = (val: unknown) => {
    this.props.customDashboardActions.setTimeRange(val);
  };

  handleRealTimeChange = (val: boolean) => {
    this.props.customDashboardActions.setRealTime(val);
  };

  toggleEditDashboard = () => {
    this.props.customDashboardActions.toggleEditDashboard();
  };

  validate = (cb: () => void, options: { excludeTitle?: boolean } = {}) => {
    const errors = this.props.customDashboardActions.validate(options);

    if (!errors) {
      cb();
    } else {
      vex.error(errors);
    }
  };

  handleSave = () => {
    this.validate(() => {
      const { dashboardId } = this.props.match.params;
      const {
        push,
        viewing,
        custom_dashboard,
        customDashboardActions,
      } = this.props;

      customDashboardActions.saveDashboard(dashboardId)
        .then(() => {
          if (!viewing) {
            (push as (path: string) => void)('/saved/dashboards');
          } else if (viewing && custom_dashboard.isEditing) {
            this.toggleEditDashboard();
          }
        })
        .catch((err: { error_description?: string }) => {
          vex.dialog.open({
            message: `<div class="vex-modal-message">${err.error_description}</div>`,
            contentClassName: 'error',
            buttons: [],
          });
        });
    });
  };

  createElement = (el: DashboardTile, index: number) => {
    const className = cx({
      'custom_dashboard_tile': true,
      'new': el.isNew,
    });

    const innerElement = this.getTileContent(el, index);

    const element = (
      <DashboardTileView
        key={el.i}
        className={className}
        onMouseDown={this.onMouseDown}
      >
        {innerElement}
      </DashboardTileView>
    );

    return element;
  };

  handleAddItem = () => {
    const { custom_dashboard: uiState } = this.props;
    const { isEditingTile } = uiState;
    if (isEditingTile) {
      return;
    }
    this.props.customDashboardActions.addTile(this.props);
  };

  render() {
    const {
      viewing,
      isShowIconPopout,
      isShowEditButton,
      custom_dashboard: uiState,
    } = this.props;
    const elements = _.map(uiState.items.slice(0), this.createElement);
    const viewOnly = !uiState.isEditing && viewing;

    // Checked before the spinner below: isLoading starts true and the load
    // failure used to leave it that way, so an unloadable dashboard (deleted,
    // or a non-numeric id) spun forever with no error. Reachable from any
    // /monitoring/custom/:dashboardId or /popout/custom-dashboard/:dashboardId
    // whose id no longer resolves.
    if (uiState.loadFailed) {
      return <ErrorPlaceholder isAbsolutePosition />;
    }

    if (!uiState.isLoading) {
      return (
        <>
          <CustomDashboardControls
            timeRange={uiState.timeRange}
            realTime={uiState.realTime}
            timezone={AppData.user.timezone}
            onTimeRangeChange={this.handleTimeRangeChange}
            onRealTimeChange={this.handleRealTimeChange}
            onTitleChange={this.handleTitleChange}
            onDescriptionChange={this.handleDescriptionChange}
            title={uiState.title}
            description={uiState.description}
            onAddItem={this.handleAddItem}
            onSave={this.handleSave}
            errors={uiState.errors}
            viewOnly={viewOnly}
            isShowEditButton={isShowEditButton}
            isShowIconPopout={isShowIconPopout}
            onOpenPopout={this.onOpenPopout}
            editDashboard={this.toggleEditDashboard}
          />
          <ReactGridLayout
            {...this.props}
            layout={uiState.layout}
            draggableCancel=".Popover"
            onLayoutChange={this.onLayoutChange}
          >
            {elements}
          </ReactGridLayout>
        </>
      );
    }
    return <DefaultSpinner />;
  }
}

// connector.js is still JavaScript, so connect() infers the injected props from
// the action module itself rather than from CustomDashboardActions. Declare the
// contract at the boundary instead of widening the class's own props.
const connector = connect(mapStateToProps, mapDispatchToProps) as unknown as (
  component: React.ComponentType<CustomDashboardProps>
) => React.ComponentType<Partial<CustomDashboardProps>>;

export default connector(CustomDashboard);
